// Failure recovery mechanisms
// Handles IndexedDB corruption, quota exceeded, and other failure scenarios

import { db, cacheStore, imageStore, metadataStore, syncQueueStore } from '../db/database.js';
import { removeToken } from '../../services/storage/localStorage.service.js';
import { OfflineConfig } from '../config/offline-config.js';
import { validateIntegrity, recoverCorruptedState } from '../db/transactional.js';

class RecoveryManager {
  constructor() {
    this.recoveryAttempts = 0;
    this.lastRecoveryTime = 0;
    this.isRecovering = false;
  }

  /**
   * Check if recovery is needed
   */
  async checkRecoveryNeeded() {
    const issues = [];

    // Check for quota exceeded
    const quotaExceeded = await this.checkQuotaExceeded();
    if (quotaExceeded) {
      issues.push('quota_exceeded');
    }

    // Check for corruption
    const corruptionDetected = await this.checkCorruption();
    if (corruptionDetected) {
      issues.push('corruption_detected');
    }

    // Check for orphaned data
    const orphans = await this.checkOrphans();
    if (orphans.length > 0) {
      issues.push('orphans_detected');
    }

    // Check for stale data — only flag if more than corruptionThreshold fraction
    // of all cache entries have expired (not just a raw count comparison)
    const stale = await this.checkStaleData();
    const allCacheKeys = await db.getKeys(cacheStore);
    const staleRatio = allCacheKeys.length > 0 ? stale.length / allCacheKeys.length : 0;
    if (staleRatio > OfflineConfig.recovery.corruptionThreshold) {
      issues.push('excessive_stale_data');
    }

    return issues;
  }

  /**
   * Check if storage quota is exceeded
   */
  async checkQuotaExceeded() {
    try {
      const testKey = `recovery-test-${Date.now()}`;
      const testData = { test: true, timestamp: Date.now() };
      
      await db.set(testKey, testData, cacheStore);
      await db.delete(testKey, cacheStore);
      
      return false;
    } catch (error) {
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        console.error('[Recovery] Quota exceeded detected');
        return true;
      }
      return false;
    }
  }

  /**
   * Check for database corruption
   */
  async checkCorruption() {
    try {
      const stats = await db.getStats();
      
      // Check if database is accessible
      if (!stats) {
        return true;
      }

      // Sample check - try to read a few entries
      const keys = await db.getKeys(cacheStore);
      if (keys.length > 0) {
        try {
          await db.get(keys[0], cacheStore);
        } catch (error) {
          console.error('[Recovery] Corruption detected in cache store');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('[Recovery] Corruption check failed:', error);
      return true;
    }
  }

  /**
   * Check for orphaned data
   */
  async checkOrphans() {
    const orphans = [];

    try {
      // Check for queue entries without corresponding entities
      const queueKeys = await db.getKeys(syncQueueStore);
      const mutationKeys = queueKeys.filter(key => key.startsWith('mutation:'));

      for (const key of mutationKeys) {
        const entry = await db.get(key, syncQueueStore);
        if (entry?.data) {
          const { entityType, entityId } = entry.data;
          const status = entry.data.status || entry.metadata?.status;

          // Skip pending/processing mutations — entities created offline won't have cache entries yet
          if (status === 'pending' || status === 'processing') continue;

          const entityKey = `${entityType}:${entityId}`;
          
          try {
            const entity = await db.get(entityKey, cacheStore);
            if (!entity && status !== 'synced') {
              orphans.push({ type: 'orphaned_queue', key, entityType, entityId });
            }
          } catch (error) {
            orphans.push({ type: 'orphaned_queue', key, entityType, entityId, error });
          }
        }
      }

      return orphans;
    } catch (error) {
      console.error('[Recovery] Orphan check failed:', error);
      return [];
    }
  }

  /**
   * Check for stale data
   */
  async checkStaleData() {
    const stale = [];
    const now = Date.now();

    try {
      const keys = await db.getKeys(cacheStore);
      
      for (const key of keys) {
        try {
          const entry = await db.get(key, cacheStore);
          if (entry?.metadata?.expiresAt && entry.metadata.expiresAt < now) {
            stale.push(key);
          }
        } catch (error) {
          stale.push(key);
        }
      }

      return stale;
    } catch (error) {
      console.error('[Recovery] Stale data check failed:', error);
      return [];
    }
  }

  /**
   * Perform recovery
   */
  async recover() {
    if (this.isRecovering) {
      console.log('[Recovery] Already recovering');
      return { success: false, message: 'Already recovering' };
    }

    // Rate limit recovery attempts
    const timeSinceLastRecovery = Date.now() - this.lastRecoveryTime;
    if (timeSinceLastRecovery < OfflineConfig.recovery.recoveryDelay) {
      console.log('[Recovery] Recovery rate limited');
      return { success: false, message: 'Rate limited' };
    }

    this.isRecovering = true;
    this.lastRecoveryTime = Date.now();

    try {
      const issues = await this.checkRecoveryNeeded();
      console.log('[Recovery] Issues detected:', issues);

      const results = {};

      // Handle quota exceeded
      if (issues.includes('quota_exceeded')) {
        results.quota = await this.recoverQuotaExceeded();
      }

      // Handle corruption
      if (issues.includes('corruption_detected')) {
        results.corruption = await this.recoverCorruption();
      }

      // Handle orphans
      if (issues.includes('orphans_detected')) {
        results.orphans = await this.recoverOrphans();
      }

      // Handle stale data
      if (issues.includes('excessive_stale_data')) {
        results.stale = await this.recoverStaleData();
      }

      this.recoveryAttempts++;
      console.log('[Recovery] Recovery complete:', results);

      return { success: true, results };
    } catch (error) {
      console.error('[Recovery] Recovery failed:', error);
      return { success: false, error };
    } finally {
      this.isRecovering = false;
    }
  }

  /**
   * Recover from quota exceeded
   */
  async recoverQuotaExceeded() {
    console.log('[Recovery] Recovering from quota exceeded');

    // Cleanup expired cache entries
    const stale = await this.checkStaleData();
    let cleaned = 0;

    for (const key of stale) {
      try {
        await db.delete(key, cacheStore);
        cleaned++;
      } catch (error) {
        console.error('[Recovery] Failed to delete stale entry:', key);
      }
    }

    // Cleanup old images
    const { imageRepository } = await import('../db/repositories.js');
    const imageCleanup = await imageRepository.cleanupOldImages(
      OfflineConfig.storage.maxImageStorageBytes * 0.7 // Reduce to 70%
    );

    // Clear disposable image cache
    try {
      await caches.delete('gym-disposable-images');
    } catch (error) {
      console.error('[Recovery] Failed to clear disposable cache:', error);
    }

    return {
      staleEntriesCleaned: cleaned,
      imagesCleaned: imageCleanup,
    };
  }

  /**
   * Recover from corruption
   */
  async recoverCorruption() {
    console.log('[Recovery] Recovering from corruption');

    // Try to validate and recover individual entities
    const keys = await db.getKeys(cacheStore);
    const recovered = [];
    const failed = [];

    for (const key of keys.slice(0, 100)) { // Limit to first 100 for performance
      try {
        const entry = await db.get(key, cacheStore);
        if (entry?.data) {
          const entityType = key.split(':')[0];
          const entityId = key.split(':')[1];
          
          const validation = await validateIntegrity(entityId, entityType);
          if (!validation.valid) {
            const recovery = await recoverCorruptedState(entityId, entityType);
            if (recovery.success) {
              recovered.push(key);
            } else {
              failed.push(key);
            }
          }
        }
      } catch (error) {
        failed.push(key);
      }
    }

    // If too many failures, clear entire cache
    if (failed.length > keys.length * OfflineConfig.recovery.corruptionThreshold) {
      console.warn('[Recovery] Excessive corruption, clearing cache');
      await db.clearStore(cacheStore);
    }

    return {
      recovered: recovered.length,
      failed: failed.length,
      cacheCleared: failed.length > keys.length * OfflineConfig.recovery.corruptionThreshold,
    };
  }

  /**
   * Recover orphans
   */
  async recoverOrphans() {
    console.log('[Recovery] Recovering orphans');

    const orphans = await this.checkOrphans();
    const recovered = [];

    for (const orphan of orphans) {
      try {
        if (orphan.type === 'orphaned_queue') {
          // Remove orphaned queue entries
          await db.delete(orphan.key, syncQueueStore);
          recovered.push(orphan.key);
        }
      } catch (error) {
        console.error('[Recovery] Failed to recover orphan:', orphan);
      }
    }

    return {
      recovered: recovered.length,
    };
  }

  /**
   * Recover stale data
   */
  async recoverStaleData() {
    console.log('[Recovery] Recovering stale data');

    const stale = await this.checkStaleData();
    let cleaned = 0;

    for (const key of stale) {
      try {
        await db.delete(key, cacheStore);
        cleaned++;
      } catch (error) {
        console.error('[Recovery] Failed to delete stale entry:', key);
      }
    }

    return {
      cleaned,
    };
  }

  /**
   * Emergency reset - clear all offline data
   * Use only as last resort
   */
  async emergencyReset() {
    console.warn('[Recovery] EMERGENCY RESET - clearing all offline data');

    try {
      await db.clearAll();
      
      // Clear all caches
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }

      // Clear memory
      if (typeof window !== 'undefined') {
        removeToken(/* force = */ true);
      }

      return { success: true, message: 'Emergency reset complete' };
    } catch (error) {
      console.error('[Recovery] Emergency reset failed:', error);
      return { success: false, error };
    }
  }

  /**
   * Get recovery statistics
   */
  async getStats() {
    const issues = await this.checkRecoveryNeeded();
    const stats = await db.getStats();

    return {
      issues,
      recoveryAttempts: this.recoveryAttempts,
      lastRecoveryTime: this.lastRecoveryTime,
      isRecovering: this.isRecovering,
      databaseStats: stats,
    };
  }
}

// Export singleton instance
export const recoveryManager = new RecoveryManager();

// Auto-recovery on initialization
if (typeof window !== 'undefined') {
  window.addEventListener('load', async () => {
    const issues = await recoveryManager.checkRecoveryNeeded();
    if (issues.length > 0) {
      console.log('[Recovery] Auto-recovery triggered for:', issues);
      await recoveryManager.recover();
    }
  });
}
